import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import Loader from "./components/ui/Loader";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
// Use real context now that backend is ready
import PrivateRoute from "./components/Login/PrivateRoute";
import { NotificationProvider } from "./contexts/NotificationContext";
import { RolePermissionProvider } from "./contexts/RolePermissionContext";
import { TimezoneProvider } from "./contexts/TimezoneContext";
import ThemeInitializer from "./components/ThemeInitializer";

// Lazy loaded components
const RootLayout = lazy(() => import("./layouts/RootLayoutEnhanced"));
const Login = lazy(() => import("./pages/Login"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Dashboard = lazy(() => import("./pages/DashboardEnhanced"));
const ProfilesLayout = lazy(() => import("./layouts/Profiles"));
const Company = lazy(() => import("./pages/profiles/company/Company"));
const CreateCompany = lazy(
  () => import("./pages/profiles/company/CreateCompanyJobStyle")
);
const EditCompany = lazy(() => import("./pages/profiles/company/EditCompanyJobStyle"));
// const CompanyDetails = lazy(
//   () => import("./pages/profiles/company/CompanyDetails")
// );
const Candidate = lazy(() => import("./pages/profiles/candidate/Candidate"));
const CreateCandidate = lazy(
  () => import("./pages/profiles/candidate/CreateCandidateJobStyle")
);
const EditCandidate = lazy(
  () => import("./pages/profiles/candidate/EditCandidateJobStyle")
);
const CandidateDetails = lazy(
  () => import("./pages/profiles/candidate/CandidateDetails")
);
const Orders = lazy(() => import("./pages/profiles/orders/Orders"));
const OrderDetails = lazy(() => import("./pages/profiles/orders/OrderDetails"));
const Request = lazy(() => import("./pages/profiles/Request"));
// const Role = lazy(() => import("./pages/profiles/Role"));
const UserRoleEnhanced = lazy(() => import("./pages/profiles/UserRoleEnhanced"));
const CreateSubAdmin = lazy(() => import("./pages/profiles/CreateSubAdmin"));
const UsersEnhanced = lazy(() => import("./pages/profiles/UsersEnhanced"));
const EditUser = lazy(() => import("./pages/profiles/EditUser"));
const JobsLayout = lazy(() => import("./layouts/Jobs"));
const JobsEnhanced = lazy(() => import("./pages/jobs/JobsEnhanced"));
const CreateJobEnhanced = lazy(() => import("./pages/jobs/CreateJobEnhanced"));
const EditJobEnhanced = lazy(() => import("./pages/jobs/EditJobEnhanced"));
const JobDetailsEnhanced = lazy(() => import("./pages/jobs/JobDetailsEnhanced"));
const JobCategoryEnhanced = lazy(() => import("./pages/jobs/JobCategoryEnhanced"));
const JobRoleEnhanced = lazy(() => import("./pages/jobs/JobRoleEnhanced"));
const CompanyPlanEnhanced = lazy(() => import("./pages/profiles/company/CompanyPlanEnhanced"));
const CandidatePlanEnhanced = lazy(
  () => import("./pages/profiles/candidate/CandidatePlanEnhanced")
);
const Attribute = lazy(() => import("./layouts/Attribute"));
const IndustryTypeEnhanced = lazy(() => import("./pages/attributes/IndustryTypeEnhanced"));
const ProfessionsEnhanced = lazy(() => import("./pages/attributes/ProfessionsEnhanced"));
const SkillsEnhanced = lazy(() => import("./pages/attributes/SkillsEnhanced"));
const TagsEnhanced = lazy(() => import("./pages/attributes/TagsEnhanced"));
const BenefitsEnhanced = lazy(() => import("./pages/attributes/BenefitsEnhanced"));
const Settings = lazy(() => import("./layouts/Settings"));
const General = lazy(() => import("./pages/settings/General"));
const LanguageSetting = lazy(() => import("./pages/settings/LanguageFinal"));
const Theme = lazy(() => import("./pages/settings/ThemeCustom"));
const Smtp = lazy(() => import("./pages/settings/Smtp"));
const Currency = lazy(() => import("./pages/settings/CurrencyFinal"));
const CountrySettings = lazy(() => import("./pages/settings/CountrySettings"));
const EmailTemplates = lazy(() => import("./pages/settings/EmailTemplates"));
const AutoPayment = lazy(() => import("./pages/settings/AutoPaymentEnhanced"));
const ManualPayment = lazy(() => import("./pages/settings/ManualPayment"));
const CookieConsent = lazy(() => import("./pages/settings/CookieConsentEnhanced"));
const MyProfile = lazy(() => import("./pages/settings/MyProfile"));
const ChangePassword = lazy(() => import("./pages/settings/ChangePassword"));
const TimezoneSettings = lazy(() => import("./pages/settings/TimezoneSettings"));
const SeoSettings = lazy(() => import("./pages/settings/SeoSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmailVerified = lazy(() => import("./pages/EmailVerified"));
const TwoFactorAuth = lazy(() => import("./pages/TwoFactorAuth"));
const TwoFactorSetup = lazy(() => import("./pages/TwoFactorSetup"));
const AllActivities = lazy(() => import("./pages/AllActivities"));
const NotificationList = lazy(() => import("./pages/notifications/NotificationList"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const App = () => {
  return (
    <AuthProvider>
      <SettingsProvider>
        <TimezoneProvider>
          <RolePermissionProvider>
            <NotificationProvider>
              <ThemeInitializer />
              <Suspense fallback={<Loader />}>
          <Routes>
          {/* Root route - for landing page, login, etc. */}
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="/two-factor-auth" element={<TwoFactorAuth />} />
          <Route path="/two-factor-setup" element={<TwoFactorSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Dashboard routes with /dashboard prefix */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <RootLayout />
              </PrivateRoute>
            }
          >
          {/* Dashboard home - accessible at /dashboard */}
          <Route index element={<Dashboard />} />
          
          {/* All Activities - accessible at /dashboard/activities */}
          <Route path="activities" element={<AllActivities />} />
          
          {/* Notifications - accessible at /dashboard/notifications */}
          <Route path="notifications" element={<NotificationList />} />

          {/* Profiles routes - accessible at /dashboard/profiles/... */}
          <Route path="profiles" element={<ProfilesLayout />}>
            <Route path="company" element={<Company />} />
            <Route path="create-company" element={<CreateCompany />} />
            <Route path="company/edit/:companyId" element={<EditCompany />} />
            {/* <Route path="company/:companyId" element={<CompanyDetails />} /> */}
            <Route path="candidate" element={<Candidate />} />
            <Route path="create-candidate" element={<CreateCandidate />} />
            <Route
              path="candidate/edit/:candidateId"
              element={<EditCandidate />}
            />
            <Route
              path="candidate/:candidateId"
              element={<CandidateDetails />}
            />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:orderId" element={<OrderDetails />} />
            <Route path="request" element={<Request />} />
            <Route path="users" element={<UsersEnhanced />} />
            <Route path="create-sub-admin" element={<CreateSubAdmin />} />
            <Route path="edit-user/:userId" element={<EditUser />} />
            <Route path="user-roles" element={<UserRoleEnhanced />} />
          </Route>

          {/* Jobs routes - accessible at /dashboard/jobs/... */}
          <Route path="jobs" element={<JobsLayout />}>
            <Route path="list" element={<JobsEnhanced />} />
            <Route path="create-job" element={<CreateJobEnhanced />} />
            <Route path="edit/:jobId" element={<EditJobEnhanced />} />
            <Route path=":jobId" element={<JobDetailsEnhanced />} />
            <Route path="category" element={<JobCategoryEnhanced />} />
            <Route path="role" element={<JobRoleEnhanced />} />
            <Route path="priceplan">
              <Route path="company" element={<CompanyPlanEnhanced />} />
              <Route path="candidate" element={<CandidatePlanEnhanced />} />
            </Route>
          </Route>

          {/* Attributes routes - accessible at /dashboard/attributes/... */}
          <Route path="attributes" element={<Attribute />}>
            <Route path="industry-type" element={<IndustryTypeEnhanced />} />
            <Route path="professions" element={<ProfessionsEnhanced />} />
            <Route path="skills" element={<SkillsEnhanced />} />
            <Route path="tags" element={<TagsEnhanced />} />
            <Route path="benefits" element={<BenefitsEnhanced />} />
          </Route>

          {/* Settings routes - accessible at /dashboard/settings/... */}
          <Route path="settings" element={<Settings />}>
            <Route path="general" element={<General />} />
            <Route path="language" element={<LanguageSetting />} />
            <Route path="theme" element={<Theme />} />
            <Route path="smtp" element={<Smtp />} />
            <Route path="currency" element={<Currency />} />
            <Route path="country" element={<CountrySettings />} />
            <Route path="timezone" element={<TimezoneSettings />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="cookie-consent" element={<CookieConsent />} />
            <Route path="seo" element={<SeoSettings />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="gateway">
              <Route path="auto" element={<AutoPayment />} />
              <Route path="manual" element={<ManualPayment />} />
            </Route>
          </Route>
        </Route>

        {/* Catch all other routes - 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </NotificationProvider>
    </RolePermissionProvider>
    </TimezoneProvider>
    </SettingsProvider>
    </AuthProvider>
  );
};

export default App;
